const isUnassignedValue = (v) => v == null || (typeof v === 'string' && v.toLowerCase() === 'unassigned');
const normalizeTypeLabel = (value) =>
  String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase();

const getYearNum = (y) => {
  if (y == null) return null;
  const str = String(y);
  const num = parseInt(str.replace(/\D/g, ''), 10);
  return Number.isNaN(num) ? null : num;
};

export function applySubjectYearFilter(items, options = {}) {
  const {
    context = 'markingCriteria',
    filterSelectedYears = [],
    filterSelectedSubjects = [],
    filterSelectedTaskTypes = [],
    filterSelectedResponseTypes = [],
    hasApiFilters = false,
  } = options;

  if (!Array.isArray(items)) return items;
  const hasUnassignedFilter = filterSelectedYears.some(y => String(y).toLowerCase() === 'unassigned')
    || filterSelectedSubjects.some(s => String(s).toLowerCase() === 'unassigned');
  if (hasApiFilters && !hasUnassignedFilter) return items;

  const hasYearFilter = filterSelectedYears.length > 0;
  const hasSubjectFilter = filterSelectedSubjects.length > 0;
  const hasTaskTypeFilter = filterSelectedTaskTypes.length > 0;
  const hasResponseTypeFilter = filterSelectedResponseTypes.length > 0;
  if (!hasYearFilter && !hasSubjectFilter && !hasTaskTypeFilter && !hasResponseTypeFilter) return items;

  return items.filter(item => {
    const rawYears = Array.isArray(item.metadata?.years) ? item.metadata.years : [];
    const itemYears = rawYears.filter((y) => !isUnassignedValue(y));
    const rawSubjects = Array.isArray(item.metadata?.subjects) ? item.metadata.subjects : [];
    const itemSubjects = rawSubjects.filter(s => {
      const code = typeof s === 'string' ? s : (s?.subjectCode ?? s?.code);
      return code != null && code !== '' && !isUnassignedValue(code);
    });
    const itemTaskTypes = Array.isArray(item.metadata?.taskTypes) ? item.metadata.taskTypes : [];
    const itemResponseTypes = Array.isArray(item.metadata?.responseTypes) ? item.metadata.responseTypes : [];

    const hasItemYears = itemYears.length > 0;
    const hasItemSubjects = itemSubjects.length > 0;
    const hasItemTaskTypes = Array.isArray(itemTaskTypes) && itemTaskTypes.length > 0;
    const hasItemResponseTypes = Array.isArray(itemResponseTypes) && itemResponseTypes.length > 0;

    if (hasYearFilter) {
      const yearMatches = filterSelectedYears.some((filterYear) => {
        if (filterYear == null || String(filterYear).toLowerCase() === 'unassigned') return !hasItemYears;
        if (!hasItemYears) return false;
        const filterYearNum = getYearNum(filterYear);
        return itemYears.some((itemYear) => {
          if (itemYear == null) return false;
          const itemYearNum = getYearNum(itemYear);
          if (filterYearNum != null && itemYearNum != null) return filterYearNum === itemYearNum;
          return String(itemYear).toLowerCase() === String(filterYear).toLowerCase();
        });
      });
      if (!yearMatches) return false;
    }

    if (hasSubjectFilter) {
      const subjectMatches = filterSelectedSubjects.some((filterSubject) => {
        if (filterSubject == null || String(filterSubject).toLowerCase() === 'unassigned') return !hasItemSubjects;
        if (!hasItemSubjects) return false;
        return itemSubjects.some((itemSubject) => {
          if (itemSubject == null) return false;
          const code = typeof itemSubject === 'string' ? itemSubject : (itemSubject?.subjectCode ?? itemSubject?.code ?? '');
          const itemSubjectStr = String(code).toLowerCase();
          const filterSubjectStr = String(filterSubject).toLowerCase();
          return itemSubjectStr && (itemSubjectStr === filterSubjectStr || itemSubjectStr.includes(filterSubjectStr) || filterSubjectStr.includes(itemSubjectStr));
        });
      });
      if (!subjectMatches) return false;
    }

    if (hasTaskTypeFilter) {
      if (!hasItemTaskTypes) return false;
      const taskTypeMatches = filterSelectedTaskTypes.some(ft =>
        itemTaskTypes.some(it =>
          normalizeTypeLabel(it) === normalizeTypeLabel(ft)
        )
      );
      if (!taskTypeMatches) return false;
    }

    if (hasResponseTypeFilter) {
      if (!hasItemResponseTypes) return false;
      const responseTypeMatches = filterSelectedResponseTypes.some(fr =>
        itemResponseTypes.some(ir =>
          normalizeTypeLabel(ir) === normalizeTypeLabel(fr)
        )
      );
      if (!responseTypeMatches) return false;
    }

    return true;
  });
}
